import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Search, Grid, List, BookOpen, Users, MapPin } from 'lucide-react';
import { allCourses } from '../data/courses';
import CourseCard from '../components/CourseCard';

const CourseList = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    let coursesToShow = [];
    
    if (category && category !== 'all') {
      // Get courses from specific category
      if (allCourses[category]) {
        coursesToShow = allCourses[category];
        
        // Reorder self-paced courses to show specific courses first
        if (category === 'self-paced') {
          const priorityCourseIds = [
            'c-cpp-dsa-combo',      // C/C++/DSA Combo
            'c-cpp-foundation',      // C/C++ Foundation
            'python-for-beginners',  // Python for Beginners
            'java-for-beginners'     // Java for Beginners
          ];
          
          // Separate priority courses and other courses
          const priorityCourses = [];
          const otherCourses = [];
          
          coursesToShow.forEach(course => {
            if (priorityCourseIds.includes(course.id)) {
              priorityCourses.push(course);
            } else {
              otherCourses.push(course);
            }
          });
          
          // Sort priority courses by the order in priorityCourseIds
          priorityCourses.sort((a, b) => {
            const aIndex = priorityCourseIds.indexOf(a.id);
            const bIndex = priorityCourseIds.indexOf(b.id);
            return aIndex - bIndex;
          });
          
          // Sort other courses by price in descending order
          otherCourses.sort((a, b) => (b.price || 0) - (a.price || 0));
          
          // Combine priority courses first, then other courses
          coursesToShow = [...priorityCourses, ...otherCourses];
        } else {
          // For all other categories, sort by price in descending order
          coursesToShow.sort((a, b) => (b.price || 0) - (a.price || 0));
        }
      }
    } else {
      // Get all courses and sort by price in descending order
      coursesToShow = Object.values(allCourses).flat();
      coursesToShow.sort((a, b) => (b.price || 0) - (a.price || 0));
    }

    setCourses(coursesToShow);
    setFilteredCourses(coursesToShow);
  }, [category]);

  useEffect(() => {
    let filtered = courses;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by level
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(course =>
        course.level.toLowerCase().includes(selectedLevel.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(course => course.category === selectedType);
    }

    setFilteredCourses(filtered);
  }, [courses, searchTerm, selectedLevel, selectedType]);

  const getCategoryTitle = () => {
    const categoryTitles = {
      'all': 'All Courses',
      'live': 'Live Courses',
      'self-paced': 'Self-Paced Courses',
      'foundations': 'Foundation Courses',
      'offline': 'Offline Classes',
      // 'free': 'Free Courses',
      'dac': "OSOPCoding's Certification Programs",
      'aiml': 'AI & Machine Learning Specialization',
      'certification': 'Certification Courses',
      'dsa-placements': 'DSA & Placement Courses',
      'development': 'Development & Testing',
      'ml-ds': 'Machine Learning & Data Science',
      'programming': 'Programming Languages',
      'cloud-devops': 'Cloud & DevOps',
      'gate': 'GATE Preparation'
    };
    return categoryTitles[category] || 'Courses';
  };

  // Add a function to get custom content for each category
  const getCategoryDescription = () => {
    if (category === 'self-paced') {
      return {
        icon: <BookOpen className="w-7 h-7 text-primary-500 mr-3" />, 
        text: 'Learn at your own pace with flexible, self-paced courses.'
      };
    }
    if (category === 'live') {
      return {
        icon: <Users className="w-7 h-7 text-primary-500 mr-3" />, 
        text: 'Join interactive live sessions with expert mentors.'
      };
    }
    if (category === 'offline') {
      return {
        icon: <MapPin className="w-7 h-7 text-primary-500 mr-3" />, 
        text: 'Experience in-person learning with our offline classes.'
      };
    }
    return null;
  };

  const categoryDesc = getCategoryDescription();

  const levels = ['all', 'beginner', 'intermediate', 'advanced'];
  const types = ['all', 'dac', 'self-paced', 'live', 'offline', /* 'free', */ 'certification'];

  return (
    <div className="pt-20 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="container-max py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            {getCategoryTitle()}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Discover courses that match your learning goals
          </p>
        </div>

        {/* Category Description Card */}
        {categoryDesc && (
          <div className="bg-white border border-gray-200 rounded-xl shadow p-6 mb-6 flex items-center">
            {categoryDesc.icon}
            <span className="text-primary-700 text-lg font-medium">{categoryDesc.text}</span>
          </div>
        )}

        {/* Filters and Search */}
        {/* <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4"> */}
            {/* Search */}
            {/* <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div> */}

            {/* Level Filter */}
            {/* <div>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {levels.map(level => (
                  <option key={level} value={level}>
                    {level === 'all' ? 'All Levels' : level.charAt(0).toUpperCase() + level.slice(1)}
                  </option>
                ))}
              </select>
            </div> */}

            {/* Type Filter */}
            {/* <div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {types.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div> */}

          {/* View Mode and Results Count */}
          {/* <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredCourses.length} of {courses.length} courses
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div> */}

        {/* Course Grid */}
        {filteredCourses.length > 0 ? (
          <motion.div
            className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                : 'grid-cols-1'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {filteredCourses.map((course, index) => (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={viewMode === 'list' ? 'max-w-none' : ''}
              >
                <CourseCard course={course} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No courses found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your filters or search terms
            </p>
          </div>
        )}
      </div>

    </div>
  );
};

export default CourseList; 